import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import Header from '../../components/ui/Header';
import CheckoutProgress from './components/CheckoutProgress';
import ShippingForm from './components/ShippingForm';
import PaymentForm from './components/PaymentForm';
import ReviewOrder from './components/ReviewOrder';
import OrderConfirmation from './components/OrderConfirmation';
import OrderSummary from './components/OrderSummary';
import Icon from '../../components/AppIcon';

const CheckoutPage = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [formData, setFormData] = useState({
    // Shipping Information
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    address: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    saveAddress: false,
    
    // Payment Information
    paymentMethod: 'card',
    cardNumber: '',
    cardName: '',
    expiryMonth: '',
    expiryYear: '',
    cvv: '',
    sameBillingAddress: true,
    
    // Review Information
    giftMessage: '',
    subscribeNewsletter: false,
    agreeTerms: false
  });

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [currentStep]);

  const handleNextStep = () => {
    if (currentStep < 4) {
      setCurrentStep(prev => prev + 1);
    }
  };

  const handlePrevStep = (step) => {
    if (step) {
      setCurrentStep(step);
    } else {
      setCurrentStep(prev => Math.max(1, prev - 1));
    }
  };

  const handlePlaceOrder = async () => {
    setIsLoading(true);
    // Simulate order processing
    setTimeout(() => {
      setIsLoading(false);
      setCurrentStep(4);
    }, 2000);
  };

  const renderStepContent = () => {
    switch (currentStep) {
      case 1:
        return (
          <ShippingForm
            onNext={handleNextStep}
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 2:
        return (
          <PaymentForm
            onNext={handleNextStep}
            onBack={handlePrevStep}
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 3:
        return (
          <ReviewOrder
            onNext={handlePlaceOrder}
            onBack={handlePrevStep}
            formData={formData}
            setFormData={setFormData}
          />
        );
      case 4:
        return (
          <OrderConfirmation
            orderData={formData}
          />
        );
      default:
        return null;
    }
  };

  if (isLoading) {
    return (
      <>
        <Helmet>
          <title>Processing Order - ElectroShop Pro</title>
          <meta name="description" content="Processing your order securely..." />
        </Helmet>
        <div className="min-h-screen bg-background">
          <Header />
          <div className="pt-20 pb-12">
            <div className="max-w-4xl mx-auto px-6">
              <div className="flex flex-col items-center justify-center min-h-[60vh] text-center">
                <div className="quantum-loader mb-6"></div>
                <h2 className="text-2xl font-semibold text-foreground mb-2">Processing Your Order</h2>
                <p className="text-muted-foreground mb-8">
                  Please wait while we securely process your payment and confirm your order.
                </p>
                <div className="flex items-center space-x-4 text-sm text-muted-foreground">
                  <div className="flex items-center space-x-2">
                    <Icon name="Shield" size={16} className="text-success" />
                    <span>SSL Encrypted</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="Lock" size={16} className="text-primary" />
                    <span>Secure Payment</span>
                  </div>
                  <div className="flex items-center space-x-2">
                    <Icon name="CheckCircle" size={16} className="text-warning" />
                    <span>Verified Transaction</span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <Helmet>
        <title>Secure Checkout - ElectroShop Pro</title>
        <meta name="description" content="Complete your purchase securely with multiple payment options and fast shipping." />
        <meta name="keywords" content="checkout, secure payment, electronics, shopping cart, order" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        
        <div className="pt-20 pb-12">
          <div className="max-w-7xl mx-auto px-6">
            {/* Page Header */}
            <div className="text-center mb-8">
              <h1 className="text-3xl md:text-4xl font-bold text-foreground mb-4">
                Secure Checkout
              </h1>
              <p className="text-lg text-muted-foreground max-w-2xl mx-auto">
                Complete your purchase with confidence using our secure, encrypted checkout process
              </p>
            </div>

            {/* Progress Indicator */}
            <CheckoutProgress currentStep={currentStep} />

            {/* Main Content */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
              {/* Checkout Form */}
              <div className="lg:col-span-2">
                {renderStepContent()}
              </div>

              {/* Order Summary Sidebar */}
              {currentStep < 4 && (
                <div className="lg:col-span-1">
                  <OrderSummary isSticky={true} />
                </div>
              )}
            </div>

            {/* Trust Indicators */}
            {currentStep < 4 && (
              <div className="mt-12 pt-8 border-t border-border">
                <div className="text-center mb-6">
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    Your Security is Our Priority
                  </h3>
                  <p className="text-muted-foreground">
                    We use industry-standard encryption to protect your personal and payment information
                  </p>
                </div>
                
                <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
                  <div className="text-center">
                    <div className="w-16 h-16 bg-success/20 rounded-full flex items-center justify-center mx-auto mb-3 trust-seal">
                      <Icon name="Shield" size={24} className="text-success" />
                    </div>
                    <h4 className="font-medium text-foreground mb-1">SSL Encrypted</h4>
                    <p className="text-sm text-muted-foreground">256-bit encryption</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-primary/20 rounded-full flex items-center justify-center mx-auto mb-3 trust-seal">
                      <Icon name="CreditCard" size={24} className="text-primary" />
                    </div>
                    <h4 className="font-medium text-foreground mb-1">Secure Payments</h4>
                    <p className="text-sm text-muted-foreground">PCI DSS compliant</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-warning/20 rounded-full flex items-center justify-center mx-auto mb-3 trust-seal">
                      <Icon name="RefreshCw" size={24} className="text-warning" />
                    </div>
                    <h4 className="font-medium text-foreground mb-1">Money Back</h4>
                    <p className="text-sm text-muted-foreground">30-day guarantee</p>
                  </div>
                  
                  <div className="text-center">
                    <div className="w-16 h-16 bg-secondary/20 rounded-full flex items-center justify-center mx-auto mb-3 trust-seal">
                      <Icon name="Headphones" size={24} className="text-secondary" />
                    </div>
                    <h4 className="font-medium text-foreground mb-1">24/7 Support</h4>
                    <p className="text-sm text-muted-foreground">Expert assistance</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Data Stream Animation */}
        <div className="data-stream"></div>
      </div>
    </>
  );
};

export default CheckoutPage;